// PATH: src/pages/ReferentialsPage.tsx — administration : années, niveaux/filières, classes
import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Tabs, Tag, Typography, message,
} from "antd";
import { BookOutlined, PlusOutlined, RetweetOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSchoolApi } from "../api/school";
import { useCurriculumApi, type CurriculumMode } from "../api/curriculum";
import { useTabParam } from "../hooks/useTabParam";
import ResponsiveTable from "../components/ResponsiveTable";
import ProgrammeTag from "../components/ProgrammeTag";
import type { Level, SchoolClass, SchoolYear } from "../models/school";

const cycles = ["MATERNELLE", "PRIMAIRE", "COLLEGE", "LYCEE"].map((v) => ({ value: v, label: v }));

const ReferentialsPage: React.FC = () => {
  const api = useSchoolApi();
  const curriculumApi = useCurriculumApi();
  const navigate = useNavigate();

  const [tab, setTab] = useTabParam("years");
  // Programme marocain : propose d'ajouter des niveaux du catalogue. Lecture
  // en échec = bouton absent, rien d'autre ne change.
  const [curriculumMode, setCurriculumMode] = useState<CurriculumMode>("");
  const [years, setYears] = useState<SchoolYear[]>([]);
  const [levels, setLevels] = useState<Level[]>([]);
  const [classes, setClasses] = useState<SchoolClass[]>([]);
  const [yearModal, setYearModal] = useState(false);
  const [levelModal, setLevelModal] = useState(false);
  const [classModal, setClassModal] = useState(false);
  const [reenrollFrom, setReenrollFrom] = useState<SchoolYear | null>(null);

  const load = useCallback(() => {
    api.listYears().then(setYears).catch(() => message.error("Chargement années impossible"));
    api.listLevels().then(setLevels).catch(() => undefined);
    api.listClasses().then(setClasses).catch(() => undefined);
    curriculumApi.get().then((c) => setCurriculumMode(c.mode ?? "")).catch(() => undefined);
  }, [api, curriculumApi]);

  useEffect(load, [load]);

  const apiError = (err: any) => message.error(err?.response?.data?.error || "Opération refusée");

  return (
    <div>
      <Typography.Title level={3}>Référentiels</Typography.Title>
      <Tabs
        activeKey={tab}
        onChange={setTab}
        items={[
          {
            key: "years",
            label: "Années scolaires",
            children: (
              <Card
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setYearModal(true)}>
                    Nouvelle année
                  </Button>
                }
              >
                <ResponsiveTable<SchoolYear>
                  rowKey="id"
                  dataSource={years}
                  pagination={false}
                  card={{
                    title: (y) => y.label,
                    extra: (y) => (y.is_active ? <Tag color="green">Active</Tag> : null),
                    actions: (y) => [
                      <Button key="re" size="small" type="text" icon={<RetweetOutlined />} onClick={() => setReenrollFrom(y)}>
                        Réinscrire
                      </Button>,
                      <Popconfirm key="del" title="Supprimer cette année ?" onConfirm={() => api.deleteYear(y.id).then(load).catch(apiError)}>
                        <Button size="small" type="text" danger>Supprimer</Button>
                      </Popconfirm>,
                    ],
                  }}
                  columns={[
                    { title: "Année", dataIndex: "label" },
                    {
                      title: "Active",
                      dataIndex: "is_active",
                      render: (v: boolean) => (v ? <Tag color="green">Oui</Tag> : "—"),
                    },
                    {
                      title: "Actions",
                      render: (_, y) => (
                        <Space>
                          <Button size="small" icon={<RetweetOutlined />} onClick={() => setReenrollFrom(y)}>
                            Réinscrire vers…
                          </Button>
                          <Popconfirm title="Supprimer cette année ?" onConfirm={() => api.deleteYear(y.id).then(load).catch(apiError)}>
                            <Button size="small" danger>Supprimer</Button>
                          </Popconfirm>
                        </Space>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "levels",
            label: "Niveaux & filières",
            children: (
              <Card
                extra={
                  <Space wrap>
                    {curriculumMode === "MA" && (
                      <Button icon={<BookOutlined />} onClick={() => navigate("/programme")}>
                        Ajouter depuis le programme marocain
                      </Button>
                    )}
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setLevelModal(true)}>
                      Nouveau niveau
                    </Button>
                  </Space>
                }
              >
                {/* Un niveau importé du programme marocain (l.locked) n'offre
                    aucune action : le backend refuse de toute façon (409). */}
                <ResponsiveTable<Level>
                  rowKey="id"
                  dataSource={levels}
                  pagination={false}
                  card={{
                    title: (l) => `${l.code} — ${l.name}`,
                    extra: (l) => (
                      <Space size={4}>
                        <Tag>{l.cycle}</Tag>
                        {l.locked && <ProgrammeTag />}
                      </Space>
                    ),
                    description: (l) =>
                      l.track ? <Typography.Text type="secondary" style={{ fontSize: 12 }}>Filière {l.track}</Typography.Text> : null,
                    actions: (l) => l.locked ? [] : [
                      <Popconfirm key="del" title="Supprimer ce niveau ?" onConfirm={() => api.deleteLevel(l.id).then(load).catch(apiError)}>
                        <Button size="small" type="text" danger>Supprimer</Button>
                      </Popconfirm>,
                    ],
                  }}
                  columns={[
                    { title: "Cycle", dataIndex: "cycle", render: (v: string) => <Tag>{v}</Tag> },
                    { title: "Code", dataIndex: "code" },
                    {
                      title: "Nom",
                      dataIndex: "name",
                      render: (v: string, l) => (l.locked ? <Space size={8} wrap>{v}<ProgrammeTag /></Space> : v),
                    },
                    { title: "Filière", dataIndex: "track", render: (v) => v || "—" },
                    {
                      title: "",
                      render: (_, l) => l.locked ? null : (
                        <Popconfirm title="Supprimer ce niveau ?" onConfirm={() => api.deleteLevel(l.id).then(load).catch(apiError)}>
                          <Button size="small" danger>Supprimer</Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "classes",
            label: "Classes",
            children: (
              <Card
                extra={
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setClassModal(true)}>
                    Nouvelle classe
                  </Button>
                }
              >
                <ResponsiveTable<SchoolClass>
                  rowKey="id"
                  dataSource={classes}
                  pagination={false}
                  card={{
                    title: (c) => c.label,
                    extra: (c) => <Tag>{c.level_cycle}</Tag>,
                    description: (c) => (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {c.year_label} · {c.level_name}
                        {c.capacity != null ? ` · ${c.capacity} places` : ""}
                      </Typography.Text>
                    ),
                    actions: (c) => [
                      <Popconfirm key="del" title="Supprimer cette classe ?" onConfirm={() => api.deleteClass(c.id).then(load).catch(apiError)}>
                        <Button size="small" type="text" danger>Supprimer</Button>
                      </Popconfirm>,
                    ],
                  }}
                  columns={[
                    { title: "Année", dataIndex: "year_label" },
                    { title: "Classe", dataIndex: "label" },
                    { title: "Niveau", dataIndex: "level_name" },
                    { title: "Cycle", dataIndex: "level_cycle", render: (v: string) => <Tag>{v}</Tag> },
                    { title: "Capacité", dataIndex: "capacity", render: (v) => v ?? "—" },
                    {
                      title: "",
                      render: (_, c) => (
                        <Popconfirm title="Supprimer cette classe ?" onConfirm={() => api.deleteClass(c.id).then(load).catch(apiError)}>
                          <Button size="small" danger>Supprimer</Button>
                        </Popconfirm>
                      ),
                    },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      {/* Année */}
      <Modal title="Nouvelle année scolaire" open={yearModal} onCancel={() => setYearModal(false)} footer={null} destroyOnClose>
        <Form
          layout="vertical"
          onFinish={(v) => api.createYear(v).then(() => { setYearModal(false); load(); }).catch(apiError)}
        >
          <Form.Item name="label" label="Libellé" rules={[{ required: true }]}>
            <Input placeholder="2026-2027" />
          </Form.Item>
          <Button type="primary" htmlType="submit">Créer</Button>
        </Form>
      </Modal>

      {/* Niveau */}
      <Modal title="Nouveau niveau" open={levelModal} onCancel={() => setLevelModal(false)} footer={null} destroyOnClose>
        {/* Hors lycée, ne pas transmettre une filière conservée par le formulaire. */}
        <Form
          layout="vertical"
          initialValues={{ cycle: "PRIMAIRE", sort_order: 0 }}
          onFinish={(v) => api.createLevel({ ...v, track: v.cycle === "LYCEE" ? v.track : undefined }).then(() => { setLevelModal(false); load(); }).catch(apiError)}
        >
          <Form.Item name="cycle" label="Cycle" rules={[{ required: true }]}>
            <Select options={cycles} />
          </Form.Item>
          <Form.Item name="code" label="Code" rules={[{ required: true }]}>
            <Input placeholder="6AP, 3AC, 2BAC-PC…" />
          </Form.Item>
          <Form.Item name="name" label="Nom" rules={[{ required: true }]}>
            <Input placeholder="6e année primaire" />
          </Form.Item>
          <Form.Item noStyle dependencies={["cycle"]}>
            {({ getFieldValue }) => (
              <Form.Item name="track" label="Filière (lycée)">
                <Input placeholder="PC, SVT, SM-A, LETTRES, ECO…" disabled={getFieldValue("cycle") !== "LYCEE"} />
              </Form.Item>
            )}
          </Form.Item>
          <Form.Item name="sort_order" label="Ordre d'affichage">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Button type="primary" htmlType="submit">Créer</Button>
        </Form>
      </Modal>

      {/* Classe */}
      <Modal title="Nouvelle classe" open={classModal} onCancel={() => setClassModal(false)} footer={null} destroyOnClose>
        <Form
          layout="vertical"
          onFinish={(v) => api.createClass(v).then(() => { setClassModal(false); load(); }).catch(apiError)}
        >
          <Form.Item name="school_year_id" label="Année scolaire" rules={[{ required: true }]}>
            <Select options={years.map((y) => ({ value: y.id, label: y.label }))} />
          </Form.Item>
          <Form.Item name="level_id" label="Niveau" rules={[{ required: true }]}>
            <Select options={levels.map((l) => ({ value: l.id, label: `${l.code} — ${l.name}` }))} />
          </Form.Item>
          <Form.Item name="label" label="Libellé" rules={[{ required: true }]}>
            <Input placeholder="6AP-B" />
          </Form.Item>
          <Form.Item name="capacity" label="Capacité">
            <InputNumber min={1} style={{ width: "100%" }} />
          </Form.Item>
          <Button type="primary" htmlType="submit">Créer</Button>
        </Form>
      </Modal>

      {/* Réinscription de masse */}
      <Modal
        title={`Réinscription depuis ${reenrollFrom?.label ?? ""}`}
        open={!!reenrollFrom}
        onCancel={() => setReenrollFrom(null)}
        footer={null}
        destroyOnClose
      >
        <Typography.Paragraph type="secondary">
          Reprend tous les élèves actifs dont l'inscription est validée sur {reenrollFrom?.label} et crée une
          inscription « en attente » (origine RÉINSCRIPTION) sur l'année cible. Aucun doublon n'est créé.
        </Typography.Paragraph>
        <Form
          layout="vertical"
          onFinish={(v) =>
            api
              .reenroll(reenrollFrom!.id, v.target_year_id)
              .then((res) => {
                message.success(`${res.created} réinscription(s) créée(s)`);
                setReenrollFrom(null);
              })
              .catch(apiError)
          }
        >
          <Form.Item name="target_year_id" label="Année cible" rules={[{ required: true }]}>
            <Select
              options={years.filter((y) => y.id !== reenrollFrom?.id).map((y) => ({ value: y.id, label: y.label }))}
            />
          </Form.Item>
          <Button type="primary" htmlType="submit">Lancer la réinscription</Button>
        </Form>
      </Modal>
    </div>
  );
};

export default ReferentialsPage;
